import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardHeader,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  TablePagination,
  Tooltip,
  CircularProgress,
  Chip,
  Avatar,
  Box,
  Typography,
  InputAdornment,
  TableContainer,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { FiberManualRecord as DotIcon } from '@mui/icons-material';
import Swal from 'sweetalert2';
import { SwalConfig } from '../../utils/swalConfig';
import axios from 'axios';

const VendorPage = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selected, setSelected] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [businessTypeFilter, setBusinessTypeFilter] = useState('');
  const [businessUnitFilter, setBusinessUnitFilter] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchVendors = async () => {
      setLoading(true);
      try {
        const vendorResponse = await fetch('https://apidashboard.eurekagroup.id/vendor');
        const buResponse = await fetch('https://apidashboard.eurekagroup.id/bu');
        
        const vendorsData = await vendorResponse.json();
        const buData = await buResponse.json();
  
        const buMap = {};
        buData.forEach(bu => {
          buMap[bu._id] = bu.bu_name;
        });
  
        if (Array.isArray(vendorsData)) {
          const vendorsWithBuName = vendorsData.map(vendor => ({
            ...vendor,
            bu_name: buMap[vendor.id_bu] || 'N/A',
          }));
          setVendors(vendorsWithBuName);
        } else {
          console.error('Unexpected data format:', vendorsData);
          setVendors([]);
        }
      } catch (error) {
        console.error('Error fetching vendor data:', error);
        setVendors([]);
      } finally {
        setLoading(false);
      }
    };
  
    fetchVendors();
  }, []);

  const handleAddVendor = () => {
    navigate('/kontak/vendor/add');
  };

  const handleEditVendor = (vendorId) => {
    navigate(`/kontak/vendor/edit/${vendorId}`);
  };

  const handleDelete = async (id) => {
    SwalConfig.deleteConfirm('vendor ini').then(async (result) => {
      if (result.isConfirmed) {
        const token = localStorage.getItem('token'); // Get token from localStorage
      
        try {
          await fetch('https://apidashboard.eurekagroup.id/vendor/delete', {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`, // Add Authorization header with Bearer token
            },
            body: JSON.stringify({ id }),
          });
      
          // Update the vendors list after deletion
          setVendors(vendors.filter((vendor) => vendor._id !== id));
          
          SwalConfig.success(
            'Berhasil Dihapus!',
            'Data vendor telah berhasil dihapus dari sistem.',
            2000
          );
        } catch (error) {
          console.error('Error deleting vendor:', error);
          SwalConfig.error('Error!', 'Terjadi kesalahan saat menghapus data.');
        }
      }
    });
  };
  
  const handleDeleteSelected = () => {
    selected.forEach((id) => handleDelete(id));
    setSelected([]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleBusinessTypeFilterChange = (event) => {
    setBusinessTypeFilter(event.target.value);
  };

  const handleBusinessUnitFilterChange = (event) => {
    setBusinessUnitFilter(event.target.value);
  };

  const filteredVendors = vendors.filter((vendor) => {
    const matchesSearch = vendor.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vendor.pic_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vendor.pic_position?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vendor.mobile?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vendor.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === '' || 
                         (statusFilter === 'active' && vendor.is_active === 1) ||
                         (statusFilter === 'inactive' && vendor.is_active === 0);
    const matchesBusinessType = businessTypeFilter === '' || vendor.business_type === businessTypeFilter;
    const matchesBusinessUnit = businessUnitFilter === '' || vendor.id_bu?._id === businessUnitFilter;
    
    return matchesSearch && matchesStatus && matchesBusinessType && matchesBusinessUnit;
  });

  const uniqueBusinessTypes = [...new Set(vendors.map(vendor => vendor.business_type).filter(Boolean))];
  const uniqueBusinessUnits = [...new Set(vendors.map(vendor => vendor.id_bu?._id).filter(Boolean))];

  return (
    <div className="animate-fade-in">
      <Card sx={{ boxShadow: '0 8px 32px rgba(0,0,0,0.1)', borderRadius: 3 }}>
        <CardHeader 
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }} className="shadow-lg">
                <BusinessIcon />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }} className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Manajemen Vendor
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} className="text-gray-600">
                  Kelola data vendor dengan mudah dan efisien
                </Typography>
              </Box>
            </Box>
          }
        action={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />} 
              onClick={handleAddVendor}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  transform: 'translateY(-2px)'
                }
              }}
            >
              ✨ Tambah Vendor
            </Button>
            <Button
              variant="contained"
              sx={{
                background: 'linear-gradient(45deg, #f50057, #e91e63)',
                color: 'white',
                borderRadius: 2,
                px: 3,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { 
                  background: 'linear-gradient(45deg, #c51162, #ad1457)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
              component={Link}
              to="/kontak/vendor-legality" 
              startIcon={<BusinessIcon />}
            >
              📋 Legalitas Vendor
            </Button>
          </Box>
        }
        sx={{ pb: 2 }}
        />
        <CardContent>
        {/* Filter Section */}
        <Box sx={{ 
          mb: 3, 
          p: 3, 
          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', 
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'grey.200'
        }} className="bg-gradient-to-r from-gray-50 to-blue-50 border-gray-200 shadow-sm animate-slide-up">
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Cari Vendor"
                variant="outlined"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: (theme) => theme.palette.background.paper
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth variant="outlined" size="small">
                <Select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  displayEmpty
                  startAdornment={
                    <InputAdornment position="start">
                      <FilterIcon color="action" />
                    </InputAdornment>
                  }
                  sx={{
                    borderRadius: 2,
                    bgcolor: (theme) => theme.palette.background.paper
                  }}
                >
                  <MenuItem value="">
                    <em>Semua Status</em>
                  </MenuItem>
                  <MenuItem value="active">Aktif</MenuItem>
                  <MenuItem value="inactive">Tidak Aktif</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth variant="outlined" size="small">
                <Select
                  value={businessTypeFilter}
                  onChange={handleBusinessTypeFilterChange}
                  displayEmpty
                  sx={{
                    borderRadius: 2,
                    bgcolor: (theme) => theme.palette.background.paper
                  }}
                >
                  <MenuItem value="">
                    <em>Semua Tipe Bisnis</em>
                  </MenuItem>
                  {uniqueBusinessTypes.map(type => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth variant="outlined" size="small">
                <Select
                  value={businessUnitFilter}
                  onChange={handleBusinessUnitFilterChange}
                  displayEmpty
                  sx={{
                    borderRadius: 2,
                    bgcolor: (theme) => theme.palette.background.paper
                  }}
                >
                  <MenuItem value="">
                    <em>Semua Unit Bisnis</em>
                  </MenuItem>
                  {uniqueBusinessUnits.map(buId => {
                    const bu = vendors.find(v => v.id_bu?._id === buId)?.id_bu;
                    return (
                      <MenuItem key={buId} value={buId}>
                        {bu?.bu_name || 'N/A'}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {filteredVendors.length}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Total Vendor
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Table Section */}
        <Box sx={{ 
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 2,
          overflow: 'hidden'
        }}>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center', 
              alignItems: 'center', 
              minHeight: '40vh',
              gap: 3
            }}>
              <Box sx={{ position: 'relative' }}>
                <CircularProgress 
                  size={60} 
                  thickness={4}
                  sx={{ 
                    color: 'primary.main',
                    animationDuration: '550ms'
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  <BusinessIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                </Box>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                  Memuat Data Vendor
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Mohon tunggu sebentar...
                </Typography>
              </Box>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ 
                      bgcolor: 'primary.main', 
                      color: 'white', 
                      fontWeight: 600,
                      fontSize: '0.875rem'
                    }}>
                      No
                    </TableCell>
                    <TableCell sx={{ 
                      bgcolor: 'primary.main', 
                      color: 'white', 
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      minWidth: 200
                    }}>
                      Vendor
                    </TableCell>
                    <TableCell sx={{ 
                      bgcolor: 'primary.main', 
                      color: 'white', 
                      fontWeight: 600,
                      fontSize: '0.875rem'
                    }}>
                      PIC Title
                    </TableCell>
                    <TableCell sx={{ 
                      bgcolor: 'primary.main', 
                      color: 'white', 
                      fontWeight: 600,
                      fontSize: '0.875rem'
                    }}>
                      PIC Position
                    </TableCell>
                    <TableCell sx={{ 
                      bgcolor: 'primary.main', 
                      color: 'white', 
                      fontWeight: 600,
                      fontSize: '0.875rem'
                    }}>
                      Mobile
                    </TableCell>
                    <TableCell sx={{ 
                      bgcolor: 'primary.main', 
                      color: 'white', 
                      fontWeight: 600,
                      fontSize: '0.875rem'
                    }}>
                      Tipe Bisnis
                    </TableCell>
                    <TableCell sx={{ 
                      bgcolor: 'primary.main', 
                      color: 'white', 
                      fontWeight: 600,
                      fontSize: '0.875rem'
                    }}>
                      Unit Bisnis
                    </TableCell>
                    <TableCell sx={{ 
                      bgcolor: 'primary.main', 
                      color: 'white', 
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      textAlign: 'center'
                    }}>
                      Aksi
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredVendors
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((vendor, index) => (
                    <TableRow 
                      key={vendor._id}
                      sx={{ 
                        '&:hover': { 
                          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover',
                          transform: 'scale(1.001)',
                          transition: 'all 0.2s ease'
                        },
                        '&:nth-of-type(even)': {
                          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'action.hover'
                        }
                      }}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>
                        {page * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar 
                            sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}
                          >
                            {vendor.vendor_name?.charAt(0)}
                          </Avatar>
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <DotIcon 
                                sx={{ 
                                  color: vendor.is_active === 1 ? 'success.main' : 'error.main',
                                  fontSize: 12
                                }}
                              />
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                {vendor.vendor_name}
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                              {vendor.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {vendor.pic_title || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {vendor.pic_position || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {vendor.mobile || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={vendor.business_type || 'N/A'}
                          variant="outlined"
                          color="secondary"
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={vendor.id_bu?.bu_name || 'N/A'}
                          variant="outlined"
                          color="info"
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <IconButton 
                            onClick={() => navigate(`/kontak/vendor/view/${vendor._id}`)} 
                            sx={{ 
                              color: 'success.main',
                              '&:hover': { 
                                bgcolor: 'success.light',
                                color: 'white',
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s ease'
                            }}
                            size="small"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            onClick={() => handleEditVendor(vendor._id)} 
                            sx={{ 
                              color: 'info.main',
                              '&:hover': { 
                                bgcolor: 'info.light',
                                color: 'white',
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s ease'
                            }}
                            size="small"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            onClick={() => handleDelete(vendor._id)} 
                            sx={{ 
                              color: 'error.main',
                              '&:hover': { 
                                bgcolor: 'error.light',
                                color: 'white',
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s ease'
                            }}
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredVendors.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: '1px solid',
              borderColor: 'grey.200',
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover'
            }}
          />
        </Box>
        </CardContent>
      </Card>
    </div>
  );
};

export default VendorPage;
